import { Button } from 'components/button/Button';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { OrderStatus } from 'src/types/enums';
import { confirmOrderUsingToken, useOrdersByToken } from '../../models';
import type { OrderStatusValue } from '../../endpoints/shared.types';
import { getDisplayError } from '../../../../utils/get-display-error';
import { parseURLSearchParams } from './utils';
import { SimpleErrorBoundary } from '../../../../components/SimpleErrorBoundary';
import { PageHeader } from '../../../../components/PageHeader';
import { SpinnerBlock } from '../../../../components/Spinner';
import { nullthrows } from '../../../../utils/invariant';
import { OrderSummary } from './OrderSummary';
import { useTranslation } from '../../../../contexts/translation-context';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';

export interface Props {
  parsed: ReturnType<typeof parseURLSearchParams>;
}

const OrderConfirmationPageContent: React.FC<Props> = (props) => {
  const { parsed } = props;
  const tokenId = parsed.orderId ?? parsed.tokenId ?? '';
  const { data: orderData, isLoading } = useOrdersByToken(tokenId, parsed.token);
  const [isRefusing, setIsRefusing] = useState(false);
  const [isRefused, setIsRefused] = useState(false);
  const { i18n } = useTranslation();

  if (isLoading) {
    return <SpinnerBlock message="Laden..." />;
  }

  const orders = nullthrows(orderData?.orders, 'Orders not found');

  const planningDate = parsed.planningDate ?? '';

  if (!orders.length) {
    throw new Error('Orders not found');
  }

  const handleClick = async () => {
    setIsRefusing(true);
    try {
      await confirmOrderUsingToken({
        id: tokenId,
        token: parsed.token,
        isConfirm: false,
      });
      trackEvent('order_refused', {
        token_id: tokenId,
        order_count: orders.length,
        is_planning: Boolean(planningDate),
      });
      setIsRefused(true);
      toast.success(i18n(planningDate ? 'pages.refusePlanning.planningRefused' : 'pages.refuseOrder.orderRefused'));
    } catch (err) {
      captureException(err);
      console.error(err);
      toast.error(i18n('pages.refuseOrder.orderRefusalError', { error: getDisplayError(err) }));
    } finally {
      setIsRefusing(false);
    }
  };

  const orderNumbers = orders.map((order) => order.orderNumber);
  const generalHeading = planningDate
    ? i18n('pages.refusePlanning.heading', {
        planningDate,
      })
    : i18n('pages.refuseOrder.heading', {
        orderNumber: orderNumbers.join(','),
      });

  let existingStatus: OrderStatusValue = OrderStatus.Cancelled;
  for (const order of orders) {
    if (order.status !== existingStatus) {
      if (existingStatus !== OrderStatus.Placed && order.status !== OrderStatus.Cancelled) {
        existingStatus = order.status;
      }
    }
  }

  let headingText = '';
  if (existingStatus === OrderStatus.Refused || isRefused) {
    headingText = i18n(
      planningDate ? 'pages.confirmOrderPlanningStates.headingRefused' : 'pages.confirmOrderStates.headingRefused',
    );
  } else if (existingStatus === OrderStatus.Cancelled) {
    headingText = i18n(
      planningDate ? 'pages.confirmOrderPlanningStates.headingCancelled' : 'pages.confirmOrderStates.headingCancelled',
    );
  } else if (existingStatus === OrderStatus.Confirmed) {
    headingText = i18n(
      planningDate ? 'pages.confirmOrderPlanningStates.headingConfirmed' : 'pages.confirmOrderStates.headingConfirmed',
    );
  } else if (existingStatus === OrderStatus.Placed) {
    headingText = generalHeading;
  } else {
    headingText = i18n(
      planningDate
        ? 'pages.confirmOrderPlanningStates.headingUnknownStatus'
        : 'pages.confirmOrderStates.headingUnknownStatus',
    );
  }

  return (
    <div>
      <PageHeader title={generalHeading} />

      <h1 className="heading-one mb-4">{headingText}</h1>

      {orders.map((order) => {
        return <OrderSummary key={order.id} order={order} />;
      })}

      {existingStatus === OrderStatus.Placed && !isRefused && (
        <Button color="primary" onTrigger={handleClick} isDisabled={isRefusing} isLoading={isRefusing}>
          {i18n(planningDate ? 'pages.refusePlanning.refuseButton' : 'pages.refuseOrder.refuseButton')}
        </Button>
      )}
    </div>
  );
};

const OrderConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const parsed = parseURLSearchParams(searchParams);

  return (
    <div>
      <div className="w-full mx-auto p-12 max-w-3xl">
        <SimpleErrorBoundary>
          <OrderConfirmationPageContent parsed={parsed} />
        </SimpleErrorBoundary>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
